/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useMemo, useState } from 'react'

import { useApi } from '@/hooks'
import logger from '@/utils/logger'

import type { AppGrant, PermissionsCap } from './types'
import { useAdminCustomers } from './useAdminCustomers'

const log = logger({ category: 'useAccessCap' })

interface CapLists {
  grants: AppGrant[]
  insights: string[]
}

/** GET /app/admin/access-cap */
export interface AccessCapResponse {
  /** What the token applies; null = no cap. */
  cap: CapLists | null
  customer: CapLists | null
  /** The user's specific groups; cap null = no profile, so it passes through to the customer. */
  groups: { companyId: number; name: string | null; cap: CapLists | null }[]
}

/**
 * What a user with this company and these asset groups may be granted — the company's
 * profile narrowed by the union of the groups' (MEGA-776), exactly as the token applies
 * it. Follows the form, so changing the company or the groups re-asks. `assetGroups`
 * empty = the whole company; ['*'] = every customer (staff scope).
 */
export const useAccessCap = (
  companyId: number | undefined | null,
  assetGroups: readonly (number | '*')[]
): { cap: PermissionsCap | null; access: AccessCapResponse | null; error: string } => {
  const { api } = useApi()
  const { customerName } = useAdminCustomers()
  const [access, setAccess] = useState<AccessCapResponse | null>(null)
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  const [error, setError] = useState('')
  const groupsKey = [...assetGroups].map(String).sort().join(',')
  const key = companyId === undefined || companyId === null ? null : `${String(companyId)}|${groupsKey}`

  useEffect(() => {
    if (key === null || companyId === undefined || companyId === null) {
      setAccess(null)
      setLoadedFor(null)
      setError('')
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const query = new URLSearchParams({ customerId: String(companyId), assetGroups: groupsKey })
        const response = await api.get<AccessCapResponse>({
          path: `/app/admin/access-cap?${query.toString()}`,
          origin: 'useAccessCap',
        })
        if (cancelled) return
        if (response.error || !response.data) {
          setAccess(null)
          setError(response.message || 'Could not load what this user may have')
        } else {
          setAccess(response.data)
          setError('')
        }
        setLoadedFor(key)
      } catch (ex) {
        log.error(ex)
        if (!cancelled) {
          setAccess(null)
          setError('Could not load what this user may have')
          setLoadedFor(key)
        }
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [api, companyId, groupsKey, key])

  const cap = useMemo<PermissionsCap | null>(() => {
    if (key === null || loadedFor !== key) return null
    const company = customerName(companyId)
    // Name the groups when they are what narrows, so the tooltip says where to look.
    const narrowing = (access?.groups ?? []).filter((group) => group.cap !== null)
    const companyNameLabel = narrowing.length
      ? `${narrowing.map((group) => group.name ?? `#${String(group.companyId)}`).join(' or ')} (${company})`
      : company
    if (!access?.cap) return { grants: [], insights: [], hasProfile: false, companyName: companyNameLabel }
    return { ...access.cap, hasProfile: true, companyName: companyNameLabel }
  }, [access, companyId, customerName, key, loadedFor])

  return { cap, access, error }
}

export default useAccessCap
